// External Dependencies
import React, { memo } from 'react';
import {
  Box,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  Heading,
  HStack,
  Text,
  theme,
  VStack,
} from '@chakra-ui/react';
import {
  DisplayAggregateQuestionDto,
  DisplayIndividualQuestionDto,
  QuestionAnalysis,
} from '@keyops-cep/api-client';
import { startCase } from 'lodash';

// Internal Dependencies
import { KeyOpsGhostButton } from '../../../components/ButtonComponents';
import { QuestionLabelPopover } from '../../../components/Visualization/QuestionLabelPopover';
import useFilterProvider from '../../../HOCs/Filter/useFilterProvider';
import i18n from '../../../utils/i18n';
import { AnswerDataWithDemographic } from '../types';

import { QuestionInsightsExpandable } from './QuestionInsightsExpandable';
import { QuestionInsightsStatic } from './QuestionInsightsStatic';
import { VisualizationDisplay } from './VisualizationDisplay';

const FILTER_QUESTION_TYPES = [
  'MultiChoice',
  'NumberInput',
  'Slider',
  'OpinionScale',
  'YesNo',
];

type AnalysisAndVisualizationCardProps = {
  question: DisplayIndividualQuestionDto | DisplayAggregateQuestionDto;
  questionAnalysis?: QuestionAnalysis;
  questionAnswers: AnswerDataWithDemographic[];
  isQuestionAddedToFilter: boolean;
  displayPrevAvg: boolean;
  displayDataTable?: boolean;
  displayEditToggle?: boolean;
  useQuestionComparativeModal: boolean;
  togglePrevAvgDisplay: () => void;
  toggleFilterDrawer: () => void;
  toggleDataTableDisplay?: () => void;
  toggleEditDisplay?: () => void;
};

const AnalysisAndVisualizationCard = ({
  question,
  questionAnalysis,
  questionAnswers,
  isQuestionAddedToFilter,
  displayPrevAvg,
  displayDataTable,
  displayEditToggle,
  useQuestionComparativeModal,
  togglePrevAvgDisplay,
  toggleFilterDrawer,
  toggleDataTableDisplay,
  toggleEditDisplay,
}: AnalysisAndVisualizationCardProps): JSX.Element => {
  const { addQuestionFilter } = useFilterProvider();
  const questionTypeDisplayText = `${startCase(question.type)}${
    question.subType ? ' - ' + startCase(question.subType) : ''
  }`;

  return (
    <Card h={'100%'} mt={4} className="questionCard">
      <CardHeader
        p="12px 16px"
        backgroundColor={theme.colors.gray[200]}
        borderRadius={'8px 8px 0 0'}
      >
        <Flex alignItems={'center'} justifyContent={'space-between'}>
          <Text fontSize={'xs'}>
            {i18n.t('engagementDetails.questionCards.question', {
              questionNo: question.position + 1,
            })}
          </Text>
          {FILTER_QUESTION_TYPES.includes(question.type) && (
            <KeyOpsGhostButton
              size={'sm'}
              fontSize={'xs'}
              textTransform={'uppercase'}
              onClick={() => {
                addQuestionFilter(question);
                if (!isQuestionAddedToFilter) toggleFilterDrawer();
              }}
            >
              {isQuestionAddedToFilter
                ? i18n.t(
                    'engagementDetails.questionCards.removeQuestionFilterButton'
                  )
                : i18n.t('engagementDetails.questionCards.addQueFilterBtn')}
            </KeyOpsGhostButton>
          )}
        </Flex>
        <Heading as={'h1'} size="xs" mt={1}>
          {question.label}
          {/* Display a disclaimer when the question label has been modified */}
          {question.label !== question.originalLabel && (
            <QuestionLabelPopover originalLabel={question.originalLabel} />
          )}
        </Heading>
      </CardHeader>
      <CardBody aspectRatio={{ base: 3.4 }} minH={'350px'} pt={0} pb={0}>
        {question.visualization && (
          <Box width={'100%'} height="100%">
            <Box
              w={{ base: '95%', md: '70%', lg: '100%' }}
              transition={'all 0.2s ease'}
              h={'100%'}
              mx={'auto'}
            >
              <VisualizationDisplay
                question={question}
                questionAnswers={questionAnswers}
                displayPrevAvg={displayPrevAvg}
                togglePrevAvgDisplay={togglePrevAvgDisplay}
                useQuestionComparativeModal={useQuestionComparativeModal}
                displayDataTable={displayDataTable}
                toggleDataTableDisplay={toggleDataTableDisplay}
                displayEditToggle={displayEditToggle}
                toggleEditDisplay={toggleEditDisplay}
              />
            </Box>
          </Box>
        )}
        {/* When no visualization and insights only */}
        {questionAnalysis?.content && !question.visualization && (
          <QuestionInsightsStatic
            question={question}
            questionAnalysisContent={questionAnalysis.content}
          />
        )}
      </CardBody>
      {/* Question info and insights (if visualization)*/}
      <CardFooter m={0} p={0}>
        <VStack alignItems={'start'} w={'100%'}>
          <HStack spacing={6} p="12px 16px">
            <Text fontSize={'xs'}>
              {i18n.t('engagementDetails.questionCards.questionType', {
                type: questionTypeDisplayText,
              })}
            </Text>
            <Text fontSize={'xs'}>
              {i18n.t('engagementDetails.questionCards.questionAnswered', {
                answeredNo: questionAnswers?.length,
              })}
            </Text>
          </HStack>
          {questionAnalysis?.content && question.visualization && (
            <QuestionInsightsExpandable
              question={question}
              questionAnalysisContent={questionAnalysis.content}
            />
          )}
        </VStack>
      </CardFooter>
    </Card>
  );
};

export default memo(AnalysisAndVisualizationCard);
